import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';
import { PDFDocument } from 'pdf-lib';
import { DocumentService, sharedApiClient } from '../../fetcher';
import { computeGroups } from './pdfSplitterGroups';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.mjs';

const DEFAULT_MAX_SLICE_BYTES = 4500000;

const isPdfFile = (file) => {
  if (!file) return false;
  const name = (file.name || '').toLowerCase();
  return name.endsWith('.pdf') || file.type === 'application/pdf';
};

const destroyPdfDoc = (doc) => {
  if (!doc) return;
  try { doc.destroy(); } catch (_) { /* noop */ }
};

/**
 * Loaded PDF, cut points, discarded pages, and the byte size of each slice.
 * Seam selection lives in usePdfSplitterSeam.
 */
const usePdfSplitter = ({ documentId = null, sourceLabel = null, maxSliceBytes = DEFAULT_MAX_SLICE_BYTES }) => {
  const documentService = useMemo(() => new DocumentService(sharedApiClient), []);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [cutAfter, setCutAfter] = useState(() => new Set());
  const [excludedPages, setExcludedPages] = useState(() => new Set());
  const [segmentResults, setSegmentResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isComputing, setIsComputing] = useState(false);
  const [error, setError] = useState(null);
  const [deleteSourceAfter, setDeleteSourceAfter] = useState(true);

  const srcDocRef = useRef(null);
  const pdfDocRef = useRef(null);
  const loadGeneration = useRef(0);

  const groups = useMemo(() => computeGroups(numPages, cutAfter), [numPages, cutAfter]);

  const groupIncluded = useMemo(
    () => groups.map((group) => group.filter((page) => !excludedPages.has(page))),
    [groups, excludedPages]
  );

  const segments = useMemo(() => groupIncluded.filter((pages) => pages.length > 0), [groupIncluded]);

  const pageGroupIndex = useMemo(() => {
    const map = {};
    groups.forEach((group, index) => {
      group.forEach((page) => { map[page] = index; });
    });
    return map;
  }, [groups]);

  const footerDocs = useMemo(() => {
    let docNumber = 0;
    return groups.map((group, index) => {
      const includedPages = groupIncluded[index];
      const isEmpty = includedPages.length === 0;
      if (!isEmpty) docNumber += 1;
      return { colorIndex: index, group, includedPages, isEmpty, docNumber: isEmpty ? null : docNumber };
    });
  }, [groups, groupIncluded]);

  const clearLoadedDocument = useCallback(() => {
    destroyPdfDoc(pdfDocRef.current);
    pdfDocRef.current = null;
    srcDocRef.current = null;
    setPdfFile(null);
    setPdfDoc(null);
    setNumPages(0);
    setCutAfter(new Set());
    setExcludedPages(new Set());
    setSegmentResults([]);
  }, []);

  const reset = useCallback(() => {
    loadGeneration.current += 1;
    clearLoadedDocument();
    setError(null);
    setIsLoading(false);
    setIsComputing(false);
    setDeleteSourceAfter(true);
  }, [clearLoadedDocument]);

  useEffect(() => () => {
    loadGeneration.current += 1;
    destroyPdfDoc(pdfDocRef.current);
    pdfDocRef.current = null;
  }, []);

  const loadFile = useCallback(async (file) => {
    if (!isPdfFile(file)) {
      setError('Only PDF files can be split. Please choose a PDF.');
      return;
    }

    const generation = ++loadGeneration.current;
    destroyPdfDoc(pdfDocRef.current);
    pdfDocRef.current = null;
    srcDocRef.current = null;
    setError(null);
    setCutAfter(new Set());
    setExcludedPages(new Set());
    setSegmentResults([]);
    setPdfDoc(null);
    setNumPages(0);
    setPdfFile(file);
    setIsLoading(true);

    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const srcDoc = await PDFDocument.load(bytes, { ignoreEncryption: true });
      const loadingTask = pdfjsLib.getDocument({ data: bytes.slice(0) });
      const doc = await loadingTask.promise;
      if (generation !== loadGeneration.current) {
        destroyPdfDoc(doc);
        return;
      }
      srcDocRef.current = srcDoc;
      pdfDocRef.current = doc;
      setPdfDoc(doc);
      setNumPages(doc.numPages);
    } catch (err) {
      if (generation !== loadGeneration.current) return;
      console.error('Failed to load PDF for splitting', err);
      srcDocRef.current = null;
      setError('Could not read this PDF. It may be corrupted or password-protected.');
      setPdfFile(null);
    } finally {
      if (generation === loadGeneration.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!documentId) return undefined;
    let cancelled = false;

    (async () => {
      setError(null);
      setIsLoading(true);
      try {
        let label = sourceLabel;
        if (!label) {
          try {
            const data = await documentService.fetchDocumentListData(documentId);
            label = (data?.overrideTitle || data?.documentTitle || 'document').replace(/\.pdf$/i, '');
          } catch (titleError) {
            console.error('Failed to load document title for splitting', titleError);
            label = 'document';
          }
        }
        if (cancelled) return;
        const blob = await documentService.fetchDocumentFile(documentId);
        if (cancelled) return;
        const file = new File([blob], `${label}.pdf`, { type: 'application/pdf' });
        await loadFile(file);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load document for splitting', err);
        setError('Could not load this document for splitting. Please try again.');
        setPdfFile(null);
        setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      loadGeneration.current += 1;
    };
  }, [documentId, sourceLabel, documentService, loadFile]);

  useEffect(() => {
    if (!srcDocRef.current || segments.length === 0) {
      setSegmentResults([]);
      setIsComputing(false);
      return undefined;
    }

    let cancelled = false;
    setIsComputing(true);
    const handle = setTimeout(async () => {
      try {
        const results = [];
        for (const pages of segments) {
          const newDoc = await PDFDocument.create();
          const indices = pages.map((page) => page - 1);
          const copied = await newDoc.copyPages(srcDocRef.current, indices);
          copied.forEach((p) => newDoc.addPage(p));
          const bytes = await newDoc.save();
          if (cancelled) return;
          results.push({ key: pages.join(','), pages, size: bytes.byteLength, bytes });
        }
        if (!cancelled) setSegmentResults(results);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to compute slice sizes', err);
          setError('Something went wrong while preparing the slices.');
        }
      } finally {
        if (!cancelled) setIsComputing(false);
      }
    }, 150);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [segments]);

  const toggleCut = useCallback((pageNumber) => {
    setCutAfter((prev) => {
      const next = new Set(prev);
      if (next.has(pageNumber)) next.delete(pageNumber);
      else next.add(pageNumber);
      return next;
    });
  }, []);

  const toggleExclude = useCallback((pageNumber) => {
    setExcludedPages((prev) => {
      const next = new Set(prev);
      if (next.has(pageNumber)) next.delete(pageNumber);
      else next.add(pageNumber);
      return next;
    });
  }, []);

  const toggleGroupInclusion = useCallback((groupPages) => {
    setExcludedPages((prev) => {
      const next = new Set(prev);
      const allIncluded = groupPages.every((page) => !next.has(page));
      if (allIncluded) groupPages.forEach((page) => next.add(page));
      else groupPages.forEach((page) => next.delete(page));
      return next;
    });
  }, []);

  const oversizedSegments = useMemo(
    () => segmentResults.filter((seg) => seg.size > maxSliceBytes),
    [segmentResults, maxSliceBytes]
  );

  const hasUnsplittableOversize = useMemo(
    () => oversizedSegments.some((seg) => seg.pages.length === 1),
    [oversizedSegments]
  );

  const canUpload = !isLoading && !isComputing && segmentResults.length > 0 && oversizedSegments.length === 0;

  const createSliceFiles = useCallback(() => {
    if (!canUpload || !pdfFile) return null;
    const base = (pdfFile.name || 'document').replace(/\.pdf$/i, '');
    const single = segmentResults.length === 1;
    const files = segmentResults.map((seg, index) => {
      const name = single ? `${base}.pdf` : `${base}_part${index + 1}.pdf`;
      return new File([seg.bytes], name, { type: 'application/pdf' });
    });
    const options = documentId ? { sourceDocumentId: documentId, deleteSourceAfter } : {};
    return { files, options };
  }, [canUpload, pdfFile, segmentResults, documentId, deleteSourceAfter]);

  return {
    pdfFile,
    pdfDoc,
    numPages,
    cutAfter,
    excludedPages,
    pageGroupIndex,
    footerDocs,
    segmentResults,
    isLoading,
    isComputing,
    error,
    deleteSourceAfter,
    setDeleteSourceAfter,
    maxSliceBytes,
    oversizedSegments,
    hasUnsplittableOversize,
    canUpload,
    documentCount: segments.length,
    isExistingDocMode: !!documentId,
    loadFile,
    reset,
    toggleCut,
    toggleExclude,
    toggleGroupInclusion,
    createSliceFiles,
  };
};

export default usePdfSplitter;
